import { createContext, type ReactNode, use, useMemo } from 'react';

import type { PartnerCapability, PartnerCapabilityMap } from '@repo/common';

import type { PartnerCapabilitiesSnapshotFragment } from '../../graphql/generated';

/**
 * Every capability denied. Hoisted so we don't reallocate per render and
 * frozen so callers can't mutate it. Explicit object literal (rather than a
 * loop with `as PartnerCapabilityMap`) so TypeScript fails the build when a
 * new key is added to `PartnerCapabilityMap` without updating this constant.
 *
 * Mirrors the backend `resolveCapabilities(undefined)` fallback so frontend
 * and backend agree: no snapshot is not evidence of entitlement.
 */
const DENIED_CAPABILITIES: PartnerCapabilityMap = Object.freeze({
  canDistributeInternally: false,
  canViewAnalytics: false,
  canAssignContent: false,
  canManageCohorts: false,
  canViewLicenses: false,
});

interface CapabilitiesContextValue {
  capabilities: PartnerCapabilityMap;
  /**
   * True when the provider has settled — either a real snapshot arrived or
   * the query finished without one. Components that gate visible UI on a
   * capability wait for this to avoid flicker (e.g. a STARTUP_TOOLS partner
   * briefly seeing the Members nav before it disappears).
   */
  loaded: boolean;
  /**
   * The snapshot could not be fetched. Capabilities read as denied in this
   * state, so a caller that only checks `loaded` cannot tell an outage from
   * a partner type that genuinely grants nothing — and would silently
   * redirect the user away from a page they are entitled to. PartnerLayout
   * reads this and renders an error in place of the routed page, which is
   * what keeps every capability-gated surface honest without each one
   * having to check.
   */
  unavailable: boolean;
}

const PartnerCapabilitiesContext = createContext<CapabilitiesContextValue>({
  capabilities: DENIED_CAPABILITIES,
  loaded: true,
  unavailable: false,
});

interface PartnerCapabilitiesProviderProps {
  /**
   * The capabilities snapshot returned by `GetMyPartner.myPartner.capabilities`.
   * When null/undefined every capability reads as denied. Whether that means
   * "still loading", "loaded but no partner", or "could not load" is carried
   * by `loading` and `error` rather than by the absent value — an admin with
   * no partner attachment is the second case and must not be stuck on a
   * permanent loader or shown an error.
   */
  value: PartnerCapabilitiesSnapshotFragment | null | undefined;
  /** True while the upstream query is in flight. */
  loading?: boolean;
  /** True when the upstream query failed to return a snapshot. */
  error?: boolean;
  /**
   * True when that failure was the backend refusing because the account has
   * no partner, rather than the backend being unreachable. Such an account is
   * not in an outage — nothing is broken and a retry would change nothing —
   * so it keeps its (empty) workspace instead of being shown an error.
   */
  detached?: boolean;
  children: ReactNode;
}

export function PartnerCapabilitiesProvider({
  value,
  loading = false,
  error = false,
  detached = false,
  children,
}: PartnerCapabilitiesProviderProps) {
  // This provider wraps the whole app from PartnerLayout, which re-renders on
  // every Apollo state change. Without the memo each render hands consumers a
  // new object and re-renders every sidebar item, page gate and
  // RequireCapability even though the five booleans are unchanged.
  // Kept despite react-compiler-no-manual-memoization: this value feeds a
  // Context.Provider consumed app-wide, so a regression here (if this file
  // were ever excluded from compilation, or a future edit changes how the
  // value is produced) would silently fan out into app-wide re-renders that
  // are hard to catch without a dedicated render-count test — higher blast
  // radius than the rule's default "just remove it" guidance is worth here.
  const contextValue = useMemo<CapabilitiesContextValue>(
    () =>
      value
        ? {
            capabilities: {
              canDistributeInternally: value.canDistributeInternally,
              canViewAnalytics: value.canViewAnalytics,
              canAssignContent: value.canAssignContent,
              canManageCohorts: value.canManageCohorts,
              canViewLicenses: value.canViewLicenses,
            },
            loaded: true,
            unavailable: false,
          }
        : {
            capabilities: DENIED_CAPABILITIES,
            loaded: !loading,
            unavailable: error && !detached,
          },
    [value, loading, error, detached],
  );

  return <PartnerCapabilitiesContext.Provider value={contextValue}>{children}</PartnerCapabilitiesContext.Provider>;
}

export function useCapabilities(): PartnerCapabilityMap {
  return use(PartnerCapabilitiesContext).capabilities;
}

export function useCapability(of: PartnerCapability): boolean {
  return use(PartnerCapabilitiesContext).capabilities[of];
}

/**
 * True when the partner-capability snapshot has settled (or there's no
 * provider in the tree, e.g. on unauthenticated routes). Components that
 * hide UI based on a capability should wait for this to be true before
 * rendering, otherwise they flicker while the query loads.
 */
export function useCapabilitiesLoaded(): boolean {
  return use(PartnerCapabilitiesContext).loaded;
}

/**
 * True when the snapshot failed to load. Denials are not trustworthy in
 * this state. Read by PartnerLayout, which replaces the routed page rather
 * than letting each gated surface redirect on a denial it cannot trust.
 */
export function useCapabilitiesUnavailable(): boolean {
  return use(PartnerCapabilitiesContext).unavailable;
}
